import React, { ReactElement, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button } from "@mui/material";
import { LoaderSpinner2 } from "components";
import { ObjectAssignedEnum, ValidationResponse } from "types/validation";
import RulesTable from "pages/Products/components/RulesTable/RulesTable.component";
import { useDispatch } from "react-redux";
import { getTableAction } from "store/reducers/products/tables/tables.actions";
import { useTypedSelector } from "store";
import { GetParentPropertyName } from "modules/utility/utility";
import ValidationMessages from "components/Validator/ValidationMessages.component";

interface ValidationItemProps {
  validations: ValidationResponse[];
}

export default function ValidationItem({
  validations,
}: ValidationItemProps): ReactElement | null {
  const [showRuleTable, setShowRuleTable] = useState<boolean>(false);
  const dispatch = useDispatch();

  const getting = useTypedSelector((s) => s.products.tables.getting);
  const productLineId =
    useTypedSelector((s) => s.products.selectedProductLine?.id) ?? "";

  // May be undefined if array is empty
  const first: ValidationResponse | undefined = validations?.[0];
  const objectId: string | undefined = first?.objectId;
  const name: string = first?.objectInfo?.Name ?? "";
  const objectAssigned: ObjectAssignedEnum | undefined = first?.objectAssigned;

  // If objectId is missing, selector returns undefined; coerce to null for rendering logic
  const ruletableData = useTypedSelector((s) =>
    objectId ? s.products.tables.byId[objectId] : undefined
  );

  // Track fetch attempts per objectId to avoid loops after delete;
  const fetchTriedForIdRef = useRef<Record<string, boolean>>({});

  // Reset attempt flag when the id changes
  useEffect(() => {
    if (objectId) {
      // allow a fresh attempt for a new id
      fetchTriedForIdRef.current[objectId] = false;
    }
  }, [objectId]);

  // Fetch only once per objectId when expanded and not yet loaded
  useEffect(() => {
    if (
      showRuleTable &&
      objectAssigned === ObjectAssignedEnum.RULETABLE &&
      objectId &&
      ruletableData === undefined && // only when not yet loaded
      !getting &&
      !fetchTriedForIdRef.current[objectId] // not already tried
    ) {
      fetchTriedForIdRef.current[objectId] = true;
      dispatch(getTableAction({ tableId: objectId }));
    }
  }, [
    showRuleTable,
    objectAssigned,
    objectId,
    ruletableData,
    getting,
    dispatch,
  ]);

  // Nothing to render if there is no first item
  if (!first) return null;

  const objectName: string =
    objectAssigned === ObjectAssignedEnum.PROPERTY
      ? `Property ${name || ""}`
      : objectAssigned === ObjectAssignedEnum.RULETABLE
      ? "Table"
      : String(objectAssigned);

  const goToProperty = (): void => {
    const parentPropertyName = GetParentPropertyName(name);
    const url = `/products/${productLineId}/properties?name=${parentPropertyName}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Wrapper>
      <HeaderRow>
        <LeftCol>
          <ObjectName>{objectName}</ObjectName>
          {(objectAssigned === ObjectAssignedEnum.PROPERTY ||
            (objectAssigned === ObjectAssignedEnum.RULETABLE &&
              !showRuleTable)) && (
            <ValidationMessages validations={validations} />
          )}
        </LeftCol>

        <RightCol>
          {objectAssigned === ObjectAssignedEnum.RULETABLE && (
            <Button
              variant="outlined"
              size="small"
              onClick={(): void => setShowRuleTable((p) => !p)}
              disabled={getting}
            >
              {showRuleTable ? "hide table" : "show table"}
            </Button>
          )}

          {objectAssigned === ObjectAssignedEnum.PROPERTY && (
            <Button variant="outlined" size="small" onClick={goToProperty}>
              go to property
            </Button>
          )}
        </RightCol>
      </HeaderRow>

      {showRuleTable &&
        objectAssigned === ObjectAssignedEnum.RULETABLE &&
        objectId &&
        (ruletableData && !getting ? (
          <RulesTableWrapper>
            <RulesTable
              id={objectId}
              ruleTableData={ruletableData}
              hideCommentsAndSources
            />
          </RulesTableWrapper>
        ) : !getting ? (
          <span>Table Not Found</span>
        ) : (
          <SpinnerWrapper>
            <LoaderSpinner2 />
          </SpinnerWrapper>
        ))}
    </Wrapper>
  );
}

/* ===== styled components ===== */

const Wrapper = styled.div`
  border-top: 1px solid ${(p) => p.theme.colors.tables.borders};
  padding-top: 8px;
  padding-bottom: 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const RightCol = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ObjectName = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 6px;
`;

const RulesTableWrapper = styled.div`
  margin-top: 16px;
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
